import React, { useState, useRef, useEffect } from 'react'
import { ChevronRight, AlertTriangle, CheckCircle, Info, ArrowLeft } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Detection, HistoryItem, Category } from '@/types'
import { useCategoryContext } from '@/contexts/CategoryContext'
import { getClassColor } from '@/utils/colorUtils'

interface ValidationCounts {
  match: number
  mismatch: number
  missing: number
  unexpected: number
}

// Validation count colors matching the legend
const VALIDATION_COLORS = {
  match: 'text-emerald-600',
  mismatch: 'text-amber-500',
  missing: 'text-red-400',
  unexpected: 'text-blue-400',
}

interface MobileDetectionViewerProps {
  analysis: HistoryItem
  imageUrl: string
  validationStatus?: Map<string, 'match' | 'mismatch' | 'unexpected'>
  validationCounts?: ValidationCounts | null
  onDetectionClick: (detection: Detection) => void
  onNextSection: () => void
  onViewValidation?: () => void
  onBack?: () => void
  hasNextSection: boolean
  isLastSection: boolean
}

// Helper to convert hex to rgba
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const MobileDetectionViewer: React.FC<MobileDetectionViewerProps> = ({
  analysis,
  imageUrl,
  validationStatus,
  validationCounts,
  onDetectionClick,
  onNextSection,
  onViewValidation,
  onBack,
  hasNextSection,
  isLastSection,
}) => {
  const { getClassCategory } = useCategoryContext()
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 })
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })

  const detections = analysis.detections || []

  // Get detection box color based on class category
  const getDetectionColor = (detection: Detection) => {
    const className = detection.user_corrected_class || detection.top_classes?.[0]?.label || ''
    const category = getClassCategory(className)
    const classColor = getClassColor(className, category)

    return {
      border: classColor,
      bg: hexToRgba(classColor, 0.15),
    }
  }

  // Update container size on mount and resize
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        })
      }
    }

    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [])

  // Handle image load to get natural dimensions
  const handleImageLoad = () => {
    if (imageRef.current) {
      setImageSize({
        width: imageRef.current.naturalWidth,
        height: imageRef.current.naturalHeight,
      })
    }
  }

  // Calculate scale factor for detection boxes
  const getScaleFactor = () => {
    if (!imageSize.width || !containerSize.width) return { scaleX: 1, scaleY: 1, displayWidth: 0, displayHeight: 0 }

    const aspectRatio = imageSize.width / imageSize.height
    let displayWidth = containerSize.width
    let displayHeight = displayWidth / aspectRatio

    // If too tall, constrain by height
    if (displayHeight > containerSize.height) {
      displayHeight = containerSize.height
      displayWidth = displayHeight * aspectRatio
    }

    return {
      scaleX: displayWidth / imageSize.width,
      scaleY: displayHeight / imageSize.height,
      displayWidth,
      displayHeight,
    }
  }

  const { scaleX, scaleY, displayWidth, displayHeight } = getScaleFactor()

  // Count issues
  const issueCount = detections.filter((d) => {
    const className = d.user_corrected_class || d.top_classes?.[0]?.label || ''
    const status = validationStatus?.get(className)
    return status === 'mismatch' || status === 'unexpected' || d.is_confident === false
  }).length

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* Header with back button */}
      {onBack && (
        <div className="bg-gray-900/80 backdrop-blur-sm px-4 py-3 flex items-center gap-3 safe-area-top flex-shrink-0 border-b border-gray-800">
          <button
            onClick={onBack}
            className="p-2 -ml-2 text-white active:bg-gray-800 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <span className="text-white font-medium">Detections</span>
        </div>
      )}

      {/* Image Container */}
      <div
        ref={containerRef}
        className="flex-1 relative overflow-hidden flex items-center justify-center"
      >
        {/* Image */}
        <div
          className="relative"
          style={{ width: displayWidth || '100%', height: displayHeight || 'auto' }}
        >
          <img
            ref={imageRef}
            src={imageUrl}
            alt="Analysis"
            onLoad={handleImageLoad}
            className="w-full h-full object-contain"
          />

          {/* Detection Overlays - colored by class, no labels */}
          {imageSize.width > 0 &&
            detections.map((detection, index) => {
              const [x1, y1, x2, y2] = detection.bbox
              const colors = getDetectionColor(detection)

              return (
                <button
                  key={detection.id || index}
                  onClick={() => onDetectionClick(detection)}
                  className="absolute cursor-pointer active:opacity-80 transition-opacity"
                  style={{
                    left: x1 * scaleX,
                    top: y1 * scaleY,
                    width: (x2 - x1) * scaleX,
                    height: (y2 - y1) * scaleY,
                    border: `2px solid ${colors.border}`,
                    backgroundColor: colors.bg,
                    minWidth: 24,
                    minHeight: 24,
                  }}
                />
              )
            })}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-white border-t safe-area-bottom flex-shrink-0">
        {/* Status Row - Clickable to show validation breakdown */}
        <button
          onClick={onViewValidation}
          className="w-full px-4 py-3 border-b flex items-center justify-between active:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-gray-800">
              {detections.length}
            </span>
            <span className="text-gray-600">items detected</span>
          </div>

          <div className="flex items-center gap-2">
            {validationCounts ? (
              <div className="flex items-center gap-1 font-semibold text-sm">
                <span className={VALIDATION_COLORS.match}>{validationCounts.match}</span>
                <span className="text-gray-400">/</span>
                <span className={VALIDATION_COLORS.mismatch}>{validationCounts.mismatch}</span>
                <span className="text-gray-400">/</span>
                <span className={VALIDATION_COLORS.missing}>{validationCounts.missing}</span>
                <span className="text-gray-400">/</span>
                <span className={VALIDATION_COLORS.unexpected}>{validationCounts.unexpected}</span>
              </div>
            ) : issueCount > 0 ? (
              <div className="flex items-center gap-1.5 text-amber-600 font-medium text-sm">
                <AlertTriangle className="h-4 w-4" />
                {issueCount} need attention
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-emerald-600 font-medium text-sm">
                <CheckCircle className="h-4 w-4" />
                All validated
              </div>
            )}
            <ChevronRight className="h-4 w-4 text-gray-400" />
          </div>
        </button>

        {/* Hint for corrections */}
        {(validationCounts ? (validationCounts.mismatch + validationCounts.missing + validationCounts.unexpected > 0) : issueCount > 0) && (
          <div className="px-4 py-2 bg-amber-50 flex items-start gap-2">
            <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              Tap any detection box to correct its class. To add missing items, use the desktop version.
            </p>
          </div>
        )}

        {/* Action Button */}
        <div className="px-4 py-3">
          <button
            onClick={onNextSection}
            className={cn(
              'w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-medium',
              'bg-primary-600 text-white active:bg-primary-700 transition-colors'
            )}
          >
            {isLastSection ? (
              'View Summary'
            ) : (
              <>
                Next Section
                <ChevronRight className="h-5 w-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default MobileDetectionViewer
